import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Enums, eventTarget } from '@cornerstonejs/core';
import type { Types as CornerstoneTypes } from '@cornerstonejs/core';
import {
  annotation as csAnnotation,
  Enums as ToolEnums,
} from '@cornerstonejs/tools';
import {
  selectAllSimpsonResults,
  selectSelectedAnnotationUID,
} from '@/state/slices/measurementSession';
import { darkColors } from '@/theme/colors';
import { renderSimpsonToCanvas } from '@/utils/simpsonMethod';
import type { SimpsonResult, Point2D } from '@/utils/simpsonMethod';

interface UseSimpsonOverlayProps {
  viewport:
    | CornerstoneTypes.IStackViewport
    | CornerstoneTypes.IVideoViewport
    | null;
  renderingEngine: CornerstoneTypes.IRenderingEngine | null;
  currentFrame: number;
  dicomUuid?: string; // Filter annotations by DICOM
}

/**
 * Transforms a Simpson result from world coordinates (mm) to canvas coordinates (pixels).
 * Cornerstone 3D stores contour points in world space, but we need canvas space for rendering.
 */
function transformSimpsonResultToCanvas(
  result: SimpsonResult,
  viewport: CornerstoneTypes.IStackViewport | CornerstoneTypes.IVideoViewport
): SimpsonResult {
  // Helper to convert world point to canvas point
  const worldToCanvas = (point: Point2D): Point2D => {
    // worldToCanvas expects [x, y, z] and returns [x, y]
    const canvasPoint = viewport.worldToCanvas([
      point.x,
      point.y,
      0,
    ] as CornerstoneTypes.Point3);
    return { x: canvasPoint[0], y: canvasPoint[1] };
  };

  return {
    ...result,
    apex: worldToCanvas(result.apex),
    baseMidpoint: worldToCanvas(result.baseMidpoint),
    baseLeft: worldToCanvas(result.baseLeft),
    baseRight: worldToCanvas(result.baseRight),
    disks: result.disks.map((disk) => ({
      ...disk,
      leftPoint: worldToCanvas(disk.leftPoint),
      rightPoint: worldToCanvas(disk.rightPoint),
    })),
  };
}

/**
 * Hook that renders Simpson measurement visualizations for the current frame
 * on an overlay canvas above the Cornerstone viewport.
 * Only results matching the current frame are rendered.
 */
export function useSimpsonOverlay({
  viewport,
  // renderingEngine available for future use (e.g., refreshing viewport after overlay)
  renderingEngine: _renderingEngine,
  currentFrame,
  dicomUuid,
}: UseSimpsonOverlayProps): void {
  // Select ALL Simpson results, then filter by current frame
  const allSimpsonResults = useSelector(selectAllSimpsonResults);
  // Ref, not a dep: re-running the effect per selection would thrash the canvas.
  const selectedAnnotationUID = useSelector(selectSelectedAnnotationUID);
  const selectedAnnotationUIDRef = useRef<string | null>(selectedAnnotationUID);
  selectedAnnotationUIDRef.current = selectedAnnotationUID;
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // PERFORMANCE: Early exit if no Simpson results exist at all
  // This avoids creating/updating canvas on every frame for 16 viewports
  const hasAnySimpsonResults = allSimpsonResults.length > 0;

  // Only compute filtered results if we have any results at all
  // Wrapped in useMemo to maintain referential stability for useEffect dependencies
  // Filter by BOTH frameIndex AND dicomUuid to scope annotations to specific DICOM
  const simpsonResults = useMemo(() => {
    if (!hasAnySimpsonResults) return [];
    return allSimpsonResults.filter(
      (result) =>
        result.frameIndex === currentFrame && result.dicomUuid === dicomUuid
    );
  }, [allSimpsonResults, currentFrame, hasAnySimpsonResults, dicomUuid]);

  // Memoized render function for all results
  const renderAllResults = useCallback(() => {
    if (!viewport) return;

    const viewportElement = viewport.element as HTMLElement;
    if (!viewportElement) return;

    // Find or create the overlay canvas
    let overlayCanvas = viewportElement.querySelector<HTMLCanvasElement>(
      '.simpson-overlay-canvas'
    );

    if (!overlayCanvas) {
      overlayCanvas = document.createElement('canvas');
      overlayCanvas.className = 'simpson-overlay-canvas';
      overlayCanvas.style.position = 'absolute';
      overlayCanvas.style.top = '0';
      overlayCanvas.style.left = '0';
      overlayCanvas.style.pointerEvents = 'none';
      overlayCanvas.style.zIndex = '10';
      viewportElement.appendChild(overlayCanvas);
    }

    // Match canvas size to viewport
    const { width, height } = viewportElement.getBoundingClientRect();
    overlayCanvas.width = width;
    overlayCanvas.height = height;

    const ctx = overlayCanvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas before re-rendering ALL results
    ctx.clearRect(0, 0, width, height);

    // Every component takes the source colour so a volume reads as one (AP-772).
    simpsonResults.forEach((result) => {
      // A hidden volume hides its disks and axis too, not just the contour.
      if (
        result.annotationUID &&
        !csAnnotation.visibility.isAnnotationVisible(result.annotationUID)
      )
        return;
      const canvasResult = transformSimpsonResultToCanvas(result, viewport);
      const annotation = result.annotationUID
        ? (csAnnotation.state.getAnnotation(result.annotationUID) as
            | { data?: { isAI?: boolean } }
            | undefined)
        : undefined;
      const isAI = !!annotation?.data?.isAI;
      const isSelected =
        !!result.annotationUID &&
        result.annotationUID === selectedAnnotationUIDRef.current;
      // Inactive token is the same hue at reduced alpha — no hue change.
      const palette = isAI
        ? [darkColors.annotationAiInactive, darkColors.annotationAiActive]
        : [
            darkColors.annotationManualInactive,
            darkColors.annotationManualActive,
          ];
      const color = palette[isSelected ? 1 : 0];

      renderSimpsonToCanvas(ctx, canvasResult, {
        diskColor: color,
        axisColor: color,
        baseColor: color,
        diskOpacity: 0.8,
        // Stays under the contour (3 active / 2 inactive) so it reads strongest.
        lineWidth: isSelected ? 2 : 1.5,
      });
    });

    overlayCanvasRef.current = overlayCanvas;
  }, [simpsonResults, viewport]);

  // Render when results change or frame changes
  useEffect(() => {
    if (!viewport) return;

    // PERFORMANCE: Skip all work if no Simpson results exist globally
    // This prevents the effect from doing any work during normal video playback
    if (!hasAnySimpsonResults) {
      // Remove canvas if it exists but we no longer have any results
      if (overlayCanvasRef.current) {
        overlayCanvasRef.current.remove();
        overlayCanvasRef.current = null;
      }
      return;
    }

    // If no results for this specific frame, clear the canvas
    if (simpsonResults.length === 0) {
      if (overlayCanvasRef.current) {
        const ctx = overlayCanvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(
            0,
            0,
            overlayCanvasRef.current.width,
            overlayCanvasRef.current.height
          );
        }
      }
      return;
    }

    renderAllResults();
  }, [
    simpsonResults,
    viewport,
    renderAllResults,
    currentFrame,
    hasAnySimpsonResults,
  ]);

  // Redraw when an annotation is shown or hidden (measurement list toggle).
  useEffect(() => {
    if (!viewport || !hasAnySimpsonResults) return undefined;
    const handler = () => renderAllResults();
    eventTarget.addEventListener(
      ToolEnums.Events.ANNOTATION_VISIBILITY_CHANGE,
      handler
    );
    return () =>
      eventTarget.removeEventListener(
        ToolEnums.Events.ANNOTATION_VISIBILITY_CHANGE,
        handler
      );
  }, [viewport, hasAnySimpsonResults, renderAllResults]);

  // Redraw whenever the viewport repaints, so the disks follow the camera
  // (resize refit, layout change, pan/zoom). An element ResizeObserver isn't
  // enough: a layout change refits the camera after the pane has resized
  // (AP-875). Same events as useDopplerBaselineOverlay: CAMERA_MODIFIED for
  // stack viewports, IMAGE_RENDERED for VideoViewport, whose resetCamera()
  // doesn't fire CAMERA_MODIFIED. Only frames with volumes listen, so playback
  // doesn't redraw every frame.
  useEffect(() => {
    if (!viewport || simpsonResults.length === 0) return undefined;
    const viewportElement = viewport.element as HTMLElement | undefined;
    if (!viewportElement) return undefined;

    const events = [Enums.Events.CAMERA_MODIFIED, Enums.Events.IMAGE_RENDERED];
    events.forEach((event) =>
      viewportElement.addEventListener(event, renderAllResults)
    );
    return () =>
      events.forEach((event) =>
        viewportElement.removeEventListener(event, renderAllResults)
      );
  }, [viewport, simpsonResults, renderAllResults]);

  // Clean up overlay canvas when viewport changes or unmounts
  useEffect(
    () => () => {
      if (overlayCanvasRef.current) {
        overlayCanvasRef.current.remove();
        overlayCanvasRef.current = null;
      }
    },
    [viewport]
  );
}
